<script setup lang="ts">
// Honest placeholder for the pages the redesign hasn't reached yet.
//
// The header carries the full nav spine from day one so the structure is
// reviewable, which means these routes get visited. Better to say plainly
// that the page is being built — and offer the two things that DO work —
// than to serve a 404 or a fake page.
import { computed } from 'vue'
import { RouterLink, useRoute } from 'vue-router'

const route = useRoute()
const title = computed(() => (route.meta.title as string | undefined) ?? 'This page')
const milestone = computed(() => route.meta.milestone as string | undefined)
</script>

<template>
    <section class="in-progress">
        <div class="inner">
            <p class="kicker">Coming soon</p>
            <h1>{{ title }} is being built.</h1>
            <p class="body">
                Ask-HVAC.com is mid-redesign. This page is on the build plan<span v-if="milestone">
                    ({{ milestone }})</span
                >, and it'll land with real content rather than a placeholder — no invented prices,
                ratings, or reviews.
            </p>
            <p class="body">In the meantime, these two do work today:</p>
            <div class="actions">
                <RouterLink class="cta" to="/ask">Troubleshoot your AC →</RouterLink>
                <RouterLink class="cta cta--ghost" to="/second-opinion">
                    Get a free second opinion
                </RouterLink>
            </div>
        </div>
    </section>
</template>

<style scoped lang="scss">
.in-progress {
    background: var(--site-bg-alt);
}

.inner {
    max-width: 42rem;
    margin: 0 auto;
    padding: clamp(3rem, 9vw, 6rem) var(--site-space-lg);
    text-align: center;
}

.kicker {
    margin: 0 0 var(--site-space-sm);
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: var(--site-accent);
}

h1 {
    font-family: var(--site-font-heading);
    font-size: clamp(1.6rem, 3.4vw, 2.4rem);
    font-weight: 800;
    color: var(--site-navy);
    margin: 0 0 var(--site-space-md);
}

.body {
    margin: 0 0 var(--site-space-md);
    color: var(--site-text-muted);
    line-height: 1.6;
}

.actions {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    gap: var(--site-space-sm);
    margin-top: var(--site-space-lg);
}

.cta {
    padding: 0.7rem 1.3rem;
    border-radius: var(--site-radius-pill);
    background: var(--site-gradient-cta);
    color: #fff;
    font-weight: 700;
    font-size: 0.9rem;
    text-decoration: none;

    &:hover {
        background: var(--site-accent-strong);
    }

    &--ghost {
        background: none;
        border: 1px solid var(--site-border);
        color: var(--site-navy);

        &:hover {
            background: none;
            border-color: var(--site-blue);
            color: var(--site-blue);
        }
    }
}
</style>
